import type { DeviceChoice } from "@/engine/state/deviceChoiceStore";

/**
 * Matching a remembered device to the hardware in front of somebody now.
 *
 * The problem this exists for is that a device identity is not stable.
 * `deviceId` is issued per origin and per permission and rotates when site data
 * is cleared, so a stored one can name nothing while the headset never moved.
 * Before the microphone permission is granted the browser withholds labels and
 * blanks the identifiers, so nothing can be matched at page load at all. And a
 * device that is absent is usually absent temporarily — somebody unplugged
 * their headset, and plugging it back in is expected to return them to it.
 *
 * So the rule is not "restore the stored identifier". A remembered choice is a
 * statement about hardware, and the fields are only how it is found again.
 *
 * Nothing here decides anything about audio. What a device sounds like belongs
 * to the chain in `micChain.ts`; this only answers which one was meant.
 */

/** A device as the browser describes it, narrowed to what matching reads. */
export interface DeviceLike {
  deviceId: string;
  groupId?: string;
  label?: string;
}

/**
 * True while the browser is withholding device names.
 *
 * Asked before anything is matched. An enumeration with no names is not
 * evidence that the remembered device is gone — it is the browser declining to
 * say — and matching against one would discard a correct choice on every load
 * that beats the permission.
 */
export function namesAreReadable(devices: readonly DeviceLike[]): boolean {
  return devices.some((device) => (device.label ?? "") !== "");
}

/** What to remember about the device with this identifier, or null. */
export function rememberDevice(
  deviceId: string,
  devices: readonly DeviceLike[],
): DeviceChoice | null {
  const device = devices.find((candidate) => candidate.deviceId === deviceId);
  if (!device) return null;
  return {
    deviceId: device.deviceId,
    groupId: device.groupId ?? "",
    label: device.label ?? "",
  };
}

/**
 * The identifier the remembered choice means now, or null where the device is
 * not present.
 *
 * Tried in order of how much each field proves. The identifier is exact while
 * it lasts; the group is the browser's own statement that two entries are the
 * same piece of hardware, and it outlives a rotation; the label is what is left
 * when even the group has been reissued, and it is last because two identical
 * headsets share one.
 *
 * Null means "not here now", never "forget it". Keeping the choice is what
 * makes reconnecting a headset return to it.
 */
export function matchDevice(
  choice: DeviceChoice | null,
  devices: readonly DeviceLike[],
): string | null {
  if (!choice) return null;
  const byId = devices.find((d) => d.deviceId === choice.deviceId);
  if (byId) return byId.deviceId;

  if (choice.groupId) {
    const byGroup = devices.find((d) => d.groupId === choice.groupId);
    if (byGroup) return byGroup.deviceId;
  }

  if (choice.label) {
    const byLabel = devices.find((d) => d.label === choice.label);
    if (byLabel) return byLabel.deviceId;
  }

  return null;
}
